import React, { useMemo, useState } from 'react';
import {
    Alert,
    AlertDescription,
    AlertIcon,
    Badge,
    Box,
    Button,
    Checkbox,
    Container,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Link as ChakraLink,
    Progress,
    SimpleGrid,
    Text,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import Navbar from '../compnents/Navbar';
import Footer from '../compnents/Footer';
import axios from 'axios';
import useDocumentMeta from '../hooks/useDocumentMeta';
import { SUPPORTED_LANGUAGES, useTranslation } from '../i18n';
import {
    buildPurchaseIntentUrl,
    normalizePurchaseIntent,
    toRegistrationAttribution,
} from '../utils/purchaseIntent';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction ? process.env.REACT_APP_API_BASE_URL_PROD : process.env.REACT_APP_API_BASE_URL_DEV;

/**
 * Registration: an email and a password, nothing else.
 *
 * Until 21 October 2026 this form asked for seven fields, a composed strong
 * password and an OTP on a domain inbox before showing anything at all, and
 * produced zero registrations in the three weeks after the self-service
 * pricing went live. Name, firm name and headcount are now collected inside
 * the workspace, after the first sign-in, by WorkspaceProfilePrompt — they
 * name the workspace and appear on exported reports, so they are still asked,
 * just not as the price of looking.
 *
 * The password rule is length only. Ten characters with no composition
 * requirement is both easier to satisfy and, in practice, not weaker than
 * eight characters with mixed case and a symbol; the backend minimum is
 * eight, so the browser stays the stricter of the two.
 */

// Length is what the rule enforces; the meter is advice, never a gate.
const MINIMUM_PASSWORD_LENGTH = 10;

const getPasswordStrength = (password) => {
    if (!password || password.length < MINIMUM_PASSWORD_LENGTH) return 'weak';
    if (password.length >= 16 || /\s/.test(password)) return 'strong';
    return 'fair';
};

const darkInputProps = {
    bg: 'rgba(255, 255, 255, 0.05)',
    color: 'white',
    borderColor: 'brand.quantumBorder',
    _placeholder: { color: 'whiteAlpha.500' },
    _hover: { borderColor: 'brand.quantumAccent' },
    _focus: {
        borderColor: 'brand.quantumAccent',
        boxShadow: '0 0 0 1px var(--chakra-colors-brand-quantumAccent)',
    },
};

const RegisterPage = () => {
    const { t, language, buildLanguageHref } = useTranslation();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        referralCode: '',
    });
    const [showReferral, setShowReferral] = useState(false);
    const [loading, setLoading] = useState(false);
    const [emailExistsError, setEmailExistsError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [generalError, setGeneralError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();

    useDocumentMeta({
        title: t('register.meta.title'),
        description: t('register.meta.description'),
    });

    const purchaseIntent = useMemo(
        () => normalizePurchaseIntent(location.search),
        [location.search]
    );
    const getVerifyOtpPath = (email) => buildPurchaseIntentUrl(
        '/verify-otp',
        purchaseIntent,
        { email }
    );
    const loginHref = buildPurchaseIntentUrl('/login', purchaseIntent);
    const demoHref = `/demo?lang=${language}`;

    const passwordStrength = useMemo(() => getPasswordStrength(formData.password), [formData.password]);
    const passwordMeter = { weak: 33, fair: 66, strong: 100 }[passwordStrength];
    const passwordColor = { weak: 'red', fair: 'teal', strong: 'green' }[passwordStrength];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (fieldErrors[name]) {
            setFieldErrors((prev) => ({ ...prev, [name]: '' }));
        }
        if (generalError) setGeneralError('');
        if (emailExistsError) setEmailExistsError('');
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.email.trim()) {
            errors.email = t('register.error.emailRequired');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            errors.email = t('register.error.emailInvalid');
        }

        if (!formData.password) {
            errors.password = t('register.error.passwordRequired');
        } else if (formData.password.length < MINIMUM_PASSWORD_LENGTH) {
            errors.password = t('register.error.passwordShort');
        }

        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setEmailExistsError('');
        setFieldErrors({});
        setGeneralError('');

        if (!validateForm()) {
            toast({
                title: t('register.error.fixForm'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/auth/register`, {
                email: formData.email,
                password: formData.password,
                // Optional: kept because it feeds partner attribution. Hidden
                // behind a checkbox, so it costs a new account nothing.
                ...(formData.referralCode ? { referralCode: formData.referralCode } : {}),
                attribution: toRegistrationAttribution(purchaseIntent),
            });
            const data = response.data;

            toast({
                title: t('register.toast.success'),
                description: data.message || t('register.toast.successBody'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            sessionStorage.setItem('pendingVerificationEmail', formData.email);
            navigate(getVerifyOtpPath(formData.email), { state: { email: formData.email, fromRegistration: true } });
        } catch (error) {
            if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
                setGeneralError('Network error. Please check your internet connection and try again.');
                return;
            }

            if (!error.response) {
                setGeneralError('Server is not responding. Please try again later.');
                return;
            }

            const status = error.response?.status;
            const errorMessage = error.response?.data?.message || 'Something went wrong. Please try again.';
            const errorData = error.response?.data;

            switch (status) {
                case 400:
                    if (
                        errorMessage.toLowerCase().includes('email already exists') ||
                        errorMessage.toLowerCase().includes('already registered')
                    ) {
                        if (
                            errorMessage.toLowerCase().includes('try resending otp') ||
                            (errorMessage.toLowerCase().includes('verified') && errorMessage.toLowerCase().includes('false'))
                        ) {
                            setEmailExistsError('Email already registered but not verified. Please verify your email to continue.');
                        } else {
                            setEmailExistsError('Email already registered. Please login to your account.');
                        }
                    } else if (errorData?.errors) {
                        const backendErrors = {};
                        errorData.errors.forEach((err) => {
                            if (err.path) {
                                backendErrors[err.path] = err.msg;
                            }
                        });
                        setFieldErrors(backendErrors);
                    } else {
                        setGeneralError(errorMessage);
                    }
                    break;
                case 409:
                    setEmailExistsError(errorMessage || 'This email is already registered.');
                    break;
                case 422:
                    if (errorData?.errors) {
                        const validationErrors = {};
                        errorData.errors.forEach((err) => {
                            if (err.field) {
                                validationErrors[err.field] = err.message;
                            }
                        });
                        setFieldErrors(validationErrors);
                    } else {
                        setGeneralError('Please check your input and try again.');
                    }
                    break;
                case 429:
                    toast({
                        title: 'Too many attempts',
                        description: 'Please wait a moment before trying again.',
                        status: 'warning',
                        duration: 5000,
                        isClosable: true,
                    });
                    break;
                case 500:
                case 502:
                case 503:
                    setGeneralError(errorData?.error || errorMessage || 'Server is currently unavailable. Please try again later.');
                    break;
                default:
                    if (errorMessage.toLowerCase().includes('network') || errorMessage.toLowerCase().includes('connection')) {
                        setGeneralError('Network error. Please check your connection and try again.');
                    } else {
                        setGeneralError(errorMessage);
                    }
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResendOTP = async () => {
        if (!formData.email) {
            navigate(buildPurchaseIntentUrl('/verify-otp', purchaseIntent));
            return;
        }

        try {
            await axios.post(`${API_BASE_URL}/auth/resend-otp`, { email: formData.email });
            toast({
                title: 'Verification code sent',
                description: 'Open the OTP screen and enter the latest code from your inbox.',
                status: 'success',
                duration: 3500,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Could not resend OTP',
                description: error.response?.data?.message || 'Go to verification and use the latest code you received.',
                status: 'warning',
                duration: 4500,
                isClosable: true,
            });
        } finally {
            sessionStorage.setItem('pendingVerificationEmail', formData.email);
            navigate(getVerifyOtpPath(formData.email), {
                state: {
                    email: formData.email,
                    fromRegistration: true,
                },
            });
        }
    };

    const handleGoToLogin = () => {
        navigate(loginHref);
    };

    const steps = ['one', 'two', 'three'];

    return (
        <Box minH="100vh" display="flex" flexDirection="column" bg="brand.quantumBg">
            <Navbar />

            <Box className="grid-bg-dark quantum-mesh" flex="1" overflow="hidden">
                <Container maxW="container.xl" position="relative" zIndex={1} py={{ base: 10, md: 16 }}>
                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, md: 10 }} alignItems="start">
                        <VStack align="stretch" spacing={7}>
                            <Badge
                                alignSelf="flex-start"
                                bg="rgba(0, 212, 170, 0.1)"
                                color="brand.quantumAccent"
                                borderRadius="full"
                                px={3}
                                py={1}
                                letterSpacing="0.15em"
                                textTransform="uppercase"
                            >
                                {t('register.badge')}
                            </Badge>
                            <Box>
                                <Heading as="h1" fontSize={{ base: '34px', md: '50px' }} color="white" lineHeight="1.06">
                                    {t('register.title')}
                                </Heading>
                                <Text mt={4} color="brand.quantumMuted" fontSize="lg" maxW="2xl">
                                    {t('register.subtitle')}
                                </Text>
                            </Box>

                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                {steps.map((step, index) => (
                                    <Box
                                        key={step}
                                        bg="brand.quantumCard"
                                        borderWidth="1px"
                                        borderColor="brand.quantumBorder"
                                        borderRadius="2xl"
                                        p={4}
                                        boxShadow="0 14px 38px rgba(0, 0, 0, 0.24)"
                                    >
                                        <Text fontSize="xs" color="brand.quantumAccent" fontWeight="800" textTransform="uppercase" letterSpacing="0.12em">
                                            {index + 1}
                                        </Text>
                                        <Text mt={2} fontWeight="800" color="white">
                                            {t(`register.step.${step}.title`)}
                                        </Text>
                                        <Text mt={2} fontSize="sm" color="brand.quantumMuted">
                                            {t(`register.step.${step}.description`)}
                                        </Text>
                                    </Box>
                                ))}
                            </SimpleGrid>

                            <Box
                                bg="rgba(0, 212, 170, 0.08)"
                                borderRadius="2xl"
                                borderWidth="1px"
                                borderColor="rgba(0, 212, 170, 0.3)"
                                p={{ base: 5, md: 6 }}
                            >
                                <ChakraLink
                                    as={RouterLink}
                                    to={demoHref}
                                    color="brand.quantumAccent"
                                    fontWeight="800"
                                    fontSize="sm"
                                >
                                    {t('register.demoLink')}
                                </ChakraLink>
                            </Box>
                        </VStack>

                        <Box
                            bg="brand.quantumCard"
                            p={{ base: 6, md: 8 }}
                            borderRadius="2xl"
                            boxShadow="0 24px 80px rgba(0, 0, 0, 0.38)"
                            borderWidth="1px"
                            borderColor="brand.quantumBorder"
                        >
                            <VStack spacing={6} align="stretch">
                                <Box display="flex" justifyContent="flex-end" gap={3} fontSize="sm">
                                    {SUPPORTED_LANGUAGES.map((code) => (
                                        <ChakraLink
                                            key={code}
                                            as={RouterLink}
                                            to={buildLanguageHref(code)}
                                            color={code === language ? 'brand.quantumAccent' : 'brand.quantumMuted'}
                                            fontWeight={code === language ? '800' : '600'}
                                            aria-current={code === language ? 'true' : undefined}
                                        >
                                            {t(`common.language.${code}`)}
                                        </ChakraLink>
                                    ))}
                                </Box>

                                <Heading as="h2" size="lg" color="white" fontWeight="bold" textAlign="center">
                                    {t('register.form.title')}
                                </Heading>

                                <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                                    {generalError ? (
                                        <Alert status="error" mb={4} borderRadius="lg">
                                            <AlertIcon />
                                            <AlertDescription>{generalError}</AlertDescription>
                                        </Alert>
                                    ) : null}

                                    <VStack spacing={5} align="stretch">
                                        <FormControl isRequired isInvalid={Boolean(fieldErrors.email)}>
                                            <FormLabel fontWeight="700" color="white">{t('register.form.email')}</FormLabel>
                                            <Input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                placeholder={t('register.form.emailPlaceholder')}
                                                size="lg"
                                                autoComplete="email"
                                                {...darkInputProps}
                                                borderColor={fieldErrors.email ? 'red.300' : 'brand.quantumBorder'}
                                            />
                                            {fieldErrors.email ? (
                                                <Text color="red.300" fontSize="sm" mt={1}>{fieldErrors.email}</Text>
                                            ) : (
                                                <Text color="brand.quantumMuted" fontSize="sm" mt={1}>
                                                    {t('register.form.emailHint')}
                                                </Text>
                                            )}
                                        </FormControl>

                                        <FormControl isRequired isInvalid={Boolean(fieldErrors.password)}>
                                            <FormLabel fontWeight="700" color="white">{t('register.form.password')}</FormLabel>
                                            <Input
                                                type="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                placeholder={t('register.form.passwordPlaceholder')}
                                                size="lg"
                                                autoComplete="new-password"
                                                {...darkInputProps}
                                                borderColor={fieldErrors.password ? 'red.300' : 'brand.quantumBorder'}
                                            />
                                            <Progress
                                                value={passwordMeter}
                                                colorScheme={passwordColor}
                                                size="sm"
                                                borderRadius="full"
                                                mt={3}
                                                bg="whiteAlpha.200"
                                                aria-label="Password strength"
                                            />
                                            {fieldErrors.password ? (
                                                <Text color="red.300" fontSize="sm" mt={1}>{fieldErrors.password}</Text>
                                            ) : (
                                                <Text color="brand.quantumMuted" fontSize="sm" mt={1}>
                                                    {t('register.form.passwordStrength', {
                                                        label: t(`register.password.${passwordStrength}`),
                                                    })}{' '}
                                                    {t('register.form.passwordHint')}
                                                </Text>
                                            )}
                                        </FormControl>
                                    </VStack>

                                    <Box mt={5} bg="rgba(255, 255, 255, 0.04)" borderWidth="1px" borderColor="brand.quantumBorder" borderRadius="xl" p={4}>
                                        <Checkbox
                                            isChecked={showReferral}
                                            onChange={(event) => setShowReferral(event.target.checked)}
                                            colorScheme="teal"
                                        >
                                            <Text as="span" color="white" fontWeight="700">
                                                {t('register.form.referral')}
                                            </Text>
                                        </Checkbox>
                                        {showReferral ? (
                                            <Input
                                                mt={4}
                                                name="referralCode"
                                                value={formData.referralCode}
                                                onChange={handleChange}
                                                placeholder={t('register.form.referralPlaceholder')}
                                                size="lg"
                                                {...darkInputProps}
                                            />
                                        ) : null}
                                    </Box>

                                    <Button
                                        type="submit"
                                        size="lg"
                                        width="full"
                                        mt={8}
                                        isLoading={loading}
                                        loadingText={t('register.form.submitting')}
                                        bgGradient="linear(135deg, brand.quantumAccent, brand.quantumCyan)"
                                        color="brand.quantumBg"
                                        _hover={{ opacity: 0.9, transform: 'translateY(-1px)' }}
                                        fontSize="md"
                                        fontWeight="800"
                                        py={6}
                                    >
                                        {t('register.form.submit')}
                                    </Button>

                                    <Text mt={3} color="brand.quantumMuted" fontSize="sm" textAlign="center">
                                        {t('register.form.termsIntro')}
                                    </Text>
                                    <Text mt={1} color="brand.quantumMuted" fontSize="sm" textAlign="center">
                                        <ChakraLink href="https://www.finansee.eu/terms-of-use" isExternal color="brand.quantumAccent">
                                            {t('register.form.termsLink')}
                                        </ChakraLink>
                                        {' · '}
                                        <ChakraLink href="https://www.finansee.eu/privacy-policy" isExternal color="brand.quantumAccent">
                                            {t('register.form.privacyLink')}
                                        </ChakraLink>
                                    </Text>

                                    {emailExistsError ? (
                                        <Box mt={4} textAlign="center" bg="rgba(0, 212, 170, 0.08)" p={4} borderRadius="lg" borderWidth="1px" borderColor="rgba(0, 212, 170, 0.3)">
                                            <Text color="brand.quantumInk" mb={3}>{emailExistsError}</Text>
                                            <VStack spacing={2}>
                                                {emailExistsError.toLowerCase().includes('not verified') ? (
                                                    <Button size="sm" colorScheme="teal" variant="solid" onClick={handleResendOTP}>
                                                        Open OTP verification
                                                    </Button>
                                                ) : (
                                                    <Button size="sm" colorScheme="teal" variant="solid" onClick={handleGoToLogin}>
                                                        Go to login
                                                    </Button>
                                                )}
                                            </VStack>
                                        </Box>
                                    ) : null}

                                    <Text mt={6} textAlign="center" color="brand.quantumMuted">
                                        {t('register.form.haveAccount')}{' '}
                                        <ChakraLink
                                            as={RouterLink}
                                            to={loginHref}
                                            color="brand.quantumAccent"
                                            fontWeight="800"
                                            _hover={{ color: 'brand.quantumCyan' }}
                                        >
                                            {t('register.form.signIn')}
                                        </ChakraLink>
                                    </Text>
                                </form>
                            </VStack>
                        </Box>
                    </SimpleGrid>
                </Container>
            </Box>

            <Footer />
        </Box>
    );
};

export default RegisterPage;
